// eslint-disable-next-line no-unused-vars
import _git from 'isomorphic-git';
import http from 'isomorphic-git/http/web';
import apiClient from './apiClient';
import FS from '@isomorphic-git/lightning-fs';
import * as Diff from 'diff';

// Resolve CJS/ESM interop: webpack may put functions on .default or on the module itself
const git = _git.init ? _git : (_git.default || _git);

/**
 * Git Service - Browser-based version control using isomorphic-git,
 * synced against a REAL git server (server/routes/gitSmartHttp.js) over the
 * standard smart-HTTP protocol -- one bare repo per (fileId, languageCode).
 *
 * Unlike the previous version of this file, pull/push transfer real git
 * objects (git.fetch/git.push against the real remote) instead of
 * reconstructing server content as brand-new local commits -- so commit
 * SHAs are now consistent across every collaborator's device.
 */

const GIT_FS_NAME = 'screenwriter-git';

// Fixed filename tracked inside every per-(fileId, languageCode) repo --
// there is exactly one file per repo (the repo IS the file's history), so
// the name itself carries no information and never needs to change.
const CONTENT_FILE = 'content';

// This app's primary auth is an httpOnly cookie -- localStorage.authToken is
// only a fallback value (see apiClient.js) and isn't guaranteed to be
// present or fresh (e.g. cleared independently of the cookie session, or
// simply expired). isomorphic-git's browser http client can't send that
// cookie either (no credentials:'include' support, and the server is
// typically cross-origin), so when localStorage lacks a usable token, fetch
// a fresh one from the server via apiClient -- which, being cookie-based
// itself, can mint one for any still-valid session regardless of whether a
// bearer token exists client-side. See server/routes/auth.js's /git-token.
let _cachedGitToken = null;

async function fetchFreshGitToken() {
  try {
    const response = await apiClient.get('/api/auth/git-token');
    _cachedGitToken = response?.token || null;
  } catch (error) {
    console.warn('gitService: could not mint a fresh git auth token:', error.message);
    _cachedGitToken = null;
  }
  return _cachedGitToken;
}

async function getGitToken() {
  const stored = localStorage.getItem('authToken');
  if (stored) return stored;
  return _cachedGitToken || fetchFreshGitToken();
}

// isomorphic-git calls onAuth on the first 401 of a request, and
// onAuthFailure (not onAuth again) on a second consecutive 401 -- providing
// both means a stale localStorage token self-heals within one pull/push
// instead of failing outright. Username is a placeholder; the server only
// checks the password (see server/middleware/gitHttpAuth.js).
const getGitAuth = async () => ({ username: 'jwt', password: (await getGitToken()) || '' });
const getGitAuthAfterFailure = async () => {
  _cachedGitToken = null; // discard whatever we just tried -- it didn't work
  const token = await fetchFreshGitToken();
  if (!token) return { cancel: true };
  return { username: 'jwt', password: token };
};

class GitService {
  constructor() {
    this.fs = new FS(GIT_FS_NAME);
    this.baseDir = '/repo';
    // Keys (`${fileId}:${languageCode}`) of repos already git.init'd this
    // session -- replaces the old single `initialized` boolean now that
    // there are many repos sharing one lightning-fs instance.
    this._initializedRepos = new Set();

    // User info (from localStorage)
    this.author = {
      name: 'User',
      email: 'user@screenwriter.app'
    };

    // Single global FS-operation queue — every method that touches `this.fs`/
    // isomorphic-git shares this same lightning-fs instance, and lightning-fs
    // guards its own IndexedDB backend with an internal Mutex whose wait has a
    // timeout. A single global queue serializes every FS-touching call
    // (across every repo) so two genuinely-concurrent callers (e.g. an
    // autosave commit racing a background sync's pull/push, even on two
    // different files) never trip that internal Mutex timeout.
    this._fsQueue = Promise.resolve();
  }

  _enqueueFsOp(task) {
    // Track whether we're currently executing inside a queued task so
    // recovery code (see _logWithRecovery) knows whether it's safe to call
    // the public pull() wrapper (which re-enqueues) or must call _pullImpl
    // directly to avoid deadlocking against its own in-flight queue entry.
    const wrapped = async () => {
      this._inQueuedOp = true;
      try {
        // A hard cap on every queued task, not just this one -- this._fsQueue
        // is a single global chain shared by every gitService call across
        // every repo (see the comment above), so ANY task that hangs (most
        // realistically a push/pull stuck against a server that never
        // resolves a bad-auth retry, which has no timeout of its own inside
        // isomorphic-git) permanently wedges every operation queued after
        // it -- including ones for completely unrelated files -- since the
        // queue only advances once the current task's promise settles. A
        // per-task timeout here is what actually bounds that, as opposed to
        // a timeout wrapped around one specific caller's await, which
        // protects that caller but does nothing to un-wedge the queue for
        // everyone stuck behind it.
        return await Promise.race([
          task(),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('git operation timed out')), 20000);
          }),
        ]);
      } finally {
        this._inQueuedOp = false;
      }
    };
    const next = this._fsQueue.then(wrapped, wrapped); // run task even if the previous op rejected
    this._fsQueue = next;
    return next;
  }

  _repoKey(fileId, languageCode) {
    return `${fileId}:${languageCode}`;
  }

  _repoDir(fileId, languageCode) {
    if (!fileId || !languageCode) {
      throw new Error('gitService: fileId and languageCode are required');
    }
    return `${this.baseDir}/${fileId}/${languageCode}`;
  }

  _remoteUrl(fileId, languageCode) {
    // apiClient's default export is a thin wrapper (see apiClient.js's
    // createApiHelper) with no `.defaults` of its own -- read the same env
    // var it was constructed from instead of a nonexistent property.
    const base = (process.env.REACT_APP_SERVER_URL || 'http://localhost:3002').replace(/\/$/, '');
    return `${base}/git/${fileId}/${languageCode}`;
  }

  /**
   * Initialize the local git repo for a specific (fileId, languageCode).
   * isomorphic-git's own directory creation is recursive (mkdir -p under the
   * hood), so no manual parent-directory setup is needed here.
   */
  async init(fileId, languageCode, allowRecovery = true, mutexRetriesLeft = 3) {
    const key = this._repoKey(fileId, languageCode);
    if (this._initializedRepos.has(key)) return;
    const dir = this._repoDir(fileId, languageCode);

    try {
      // Check if repo exists — probe for the .git directory, NOT resolveRef('HEAD'):
      // in a repo with no commits yet HEAD points to refs/heads/main which doesn't
      // exist until the first commit, so resolveRef throws even on a valid repo
      try {
        await this.fs.promises.stat(`${dir}/.git`);
      } catch (err) {
        if (allowRecovery && this._isStorageCorrupted(err)) throw err; // handled below
        // Repo doesn't exist, initialize it
        await git.init({ fs: this.fs, dir, defaultBranch: 'main' });
      }

      // Register "origin" unconditionally (idempotent via force) -- git.fetch/
      // git.push resolve their remote-tracking refspec from this config entry
      // even when a `url` is passed directly on every call; without it,
      // fetch() fails with NoRefspecError ("Could not find a fetch refspec").
      // Also self-heals repos created before this was added.
      await git.addRemote({ fs: this.fs, dir, remote: 'origin', url: this._remoteUrl(fileId, languageCode), force: true });

      // Update author info from localStorage
      const user = localStorage.getItem('screenwriter_user_email');
      const userName = localStorage.getItem('screenwriter_user_name');
      if (user) this.author.email = user;
      if (userName) this.author.name = userName;

      this._initializedRepos.add(key);
    } catch (error) {
      // The IndexedDB backend lightning-fs writes to (a database entirely
      // separate from the app's own ScreenwriterDB) can end up corrupted at
      // the browser-storage level — surfaces as a generic UnknownError, not
      // a version mismatch. Left alone this fails forever; recover once by
      // wiping that backend's databases and rebuilding from scratch. This
      // affects EVERY repo (they share one lightning-fs instance), not just
      // this one -- each will lazily re-init and re-pull from the server
      // (the real source of truth) on its own next access.
      if (allowRecovery && this._isStorageCorrupted(error)) {
        console.warn('Git storage appears corrupted — recreating local git repos...');
        try {
          await this._recoverFromCorruption();
          return this.init(fileId, languageCode, false);
        } catch (recoveryError) {
          console.error('Git storage recovery failed:', recoveryError);
          throw recoveryError;
        }
      }

      // lightning-fs guards its IndexedDB backend with a cross-tab Mutex
      // that can time out transiently under load (many repos initializing
      // back-to-back) without the underlying storage actually being broken.
      // init() is the one call every gitService method makes first (read
      // methods like getHistory() call it directly, not just the queued
      // write path) -- retrying HERE, not just in the sync loop, is what
      // keeps read-only UI (e.g. Version History) working even when a sync
      // pass elsewhere is contending for the same lock.
      if (error.message === 'Mutex timeout' && mutexRetriesLeft > 0) {
        const delay = (4 - mutexRetriesLeft) * 300; // 300ms, 600ms, 900ms
        console.warn(`gitService: init() hit a Mutex timeout for ${key}, retrying (${mutexRetriesLeft} left)...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.init(fileId, languageCode, allowRecovery, mutexRetriesLeft - 1);
      }

      console.error('Failed to initialize git:', error);
      throw error;
    }
  }

  _isStorageCorrupted(error) {
    return error?.name === 'UnknownError';
  }

  async _recoverFromCorruption() {
    const deleteDb = (name) => new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve(); // best-effort — don't block recovery on this
      req.onblocked = () => resolve();
    });

    await Promise.all([deleteDb(GIT_FS_NAME), deleteDb(`${GIT_FS_NAME}_lock`)]);

    this.fs = new FS(GIT_FS_NAME);
    this._initializedRepos.clear();
  }

  // If a commit's tree/blob object never durably made it into the
  // lightning-fs IndexedDB backend (e.g. the tab was closed mid-sync), the
  // local `main` ref can end up pointing at a commit whose object graph is
  // incomplete. isomorphic-git surfaces that as a NotFoundError while
  // walking history. Since the server is the real source of truth, self-heal
  // by wiping local state and rebuilding this repo from a fresh pull, same
  // as _recoverFromCorruption does for storage-level UnknownErrors.
  _isMissingObjectError(error) {
    return error?.code === 'NotFoundError' || error?.name === 'NotFoundError';
  }

  // isomorphic-git's GitRefManager.resolve() throws the SAME NotFoundError
  // class/code for "this ref doesn't exist at all" (e.g. `main` on a repo
  // that has zero commits yet -- entirely expected right after init(), before
  // anything has ever been committed or pulled) as readObject() does for a
  // genuinely missing/corrupted object. They're distinguishable: NotFoundError
  // stores the thing it couldn't find on `.data.what` (see isomorphic-git's
  // NotFoundError constructor) -- a ref-not-found's `.data.what` is the ref
  // name itself ("main"), never a hex oid. Treating the former as corruption
  // (below) wipes EVERY local repo's IndexedDB storage and round-trips to the
  // server for what is a completely normal, benign state -- confirmed live:
  // "NotFoundError: Could not find main" on every Version History open for a
  // file whose repo has no commits yet (e.g. never autosaved/synced).
  _isMissingRefError(error, ref) {
    return this._isMissingObjectError(error) && error?.data?.what === ref;
  }

  async _logWithRecovery(fileId, languageCode, logOptions) {
    const dir = this._repoDir(fileId, languageCode);
    try {
      return await git.log({ fs: this.fs, dir, ...logOptions });
    } catch (error) {
      // Empty repo (no commits yet, locally or on the server) -- not
      // corruption, nothing to recover. Just report empty history.
      if (this._isMissingRefError(error, logOptions.ref)) {
        return [];
      }

      if (!this._isMissingObjectError(error)) throw error;

      console.warn('Git object missing from local repo (corruption) — rebuilding local history from server...', error.message);
      await this._recoverFromCorruption();
      await this.init(fileId, languageCode, false);

      // Rebuild from the server. If we're already running inside the fs
      // queue (e.g. called from within _pullImpl), calling the public
      // pull() wrapper here would enqueue a second task behind our own
      // still-running one and deadlock -- call the impl directly instead.
      if (this._inQueuedOp) {
        await this._pullImpl(fileId, languageCode);
      } else {
        await this.pull(fileId, languageCode);
      }

      // Let a second failure propagate to the caller's own catch block.
      return git.log({ fs: this.fs, dir, ...logOptions });
    }
  }

  async _readWorkingFile(dir) {
    try {
      return await this.fs.promises.readFile(`${dir}/${CONTENT_FILE}`, 'utf8');
    } catch {
      return '';
    }
  }

  /**
   * Commit file changes
   * Replaces: saveFileVersion()
   */
  async commit(fileId, languageCode, content, message) {
    // Serialize against every other FS-touching call — see _fsQueue above.
    return this._enqueueFsOp(() => this._commitImpl(fileId, languageCode, content, message));
  }

  async _commitImpl(fileId, languageCode, content, message) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);
    const filePath = `${dir}/${CONTENT_FILE}`;

    try {
      // Skip no-op commits: identical content produces an empty commit and
      // spams history (e.g. an autosave firing with unchanged content).
      try {
        const existing = await this.fs.promises.readFile(filePath, 'utf8');
        if (existing === content) {
          const head = await git.resolveRef({ fs: this.fs, dir, ref: 'HEAD' }).catch(() => null);
          if (head) {
            return { sha: head, message, skipped: true };
          }
        }
      } catch (e) {
        // File doesn't exist yet — proceed with the commit
      }

      await this.fs.promises.writeFile(filePath, content, 'utf8');
      await git.add({ fs: this.fs, dir, filepath: CONTENT_FILE });

      const sha = await git.commit({
        fs: this.fs,
        dir,
        message,
        author: this._currentAuthor()
      });

      return { sha, message };
    } catch (error) {
      console.error('Commit failed:', error);
      throw error;
    }
  }

  // The git author is the person actually logged in. It used to come from
  // two localStorage keys that nothing ever sets, so every commit -- edits and
  // merges alike -- was recorded as "User", and Version History could not say
  // who made a version.
  _currentAuthor() {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (user) {
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email;
        if (name) return { name, email: user.email || this.author.email };
      }
    } catch { /* fall through to the default author */ }
    return this.author;
  }

  // Merge commits say whose changes were merged: the merger is the commit's
  // author, and the message names each side's last author.
  _mergeMessage(remoteAuthor, localAuthor, mergedBy) {
    return `Merged changes from ${remoteAuthor}\n\n`
      + `Merged by: ${mergedBy}\n`
      + `Changes pushed by: ${remoteAuthor}\n`
      + `Changes on this device by: ${localAuthor}`;
  }

  /**
   * Get commit history for a file
   * Replaces: getFileVersions()
   */
  async getHistory(fileId, languageCode, limit = 50) {
    await this.init(fileId, languageCode);

    try {
      const commits = await this._logWithRecovery(fileId, languageCode, { ref: 'main' });

      return commits.slice(0, limit).map(commit => ({
        sha: commit.oid,
        message: commit.commit.message,
        author: commit.commit.author.name,
        email: commit.commit.author.email,
        timestamp: new Date(commit.commit.author.timestamp * 1000),
        // Map to old version format for compatibility
        version: commits.length - commits.indexOf(commit),
        file_id: fileId
      }));
    } catch (error) {
      console.error('Failed to get history:', error);
      return [];
    }
  }

  /**
   * Get file content at specific commit
   * Replaces: getFileVersion()
   */
  async getFileAtCommit(fileId, languageCode, commitSha) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      const { blob } = await git.readBlob({
        fs: this.fs,
        dir,
        oid: commitSha,
        filepath: CONTENT_FILE
      });

      return new TextDecoder().decode(blob);
    } catch (error) {
      console.error('Failed to read file at commit:', error);
      throw error;
    }
  }

  /**
   * Get diff between two commits
   * Replaces: DiffViewer custom logic
   */
  async getDiff(fileId, languageCode, oldCommit, newCommit = 'HEAD') {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      const oldContent = oldCommit
        ? await this.getFileAtCommit(fileId, languageCode, oldCommit)
        : '';

      const newContent = newCommit === 'HEAD'
        ? await this._readWorkingFile(dir)
        : await this.getFileAtCommit(fileId, languageCode, newCommit);

      return { oldContent, newContent, oldCommit, newCommit };
    } catch (error) {
      console.error('Failed to get diff:', error);
      throw error;
    }
  }

  /**
   * Create a branch (for collaboration)
   */
  async createBranch(fileId, languageCode, branchName) {
    return this._enqueueFsOp(() => this._createBranchImpl(fileId, languageCode, branchName));
  }

  async _createBranchImpl(fileId, languageCode, branchName) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      await git.branch({ fs: this.fs, dir, ref: branchName });
      return true;
    } catch (error) {
      console.error('Failed to create branch:', error);
      throw error;
    }
  }

  /**
   * Switch branches
   */
  async checkout(fileId, languageCode, branchName) {
    return this._enqueueFsOp(() => this._checkoutImpl(fileId, languageCode, branchName));
  }

  async _checkoutImpl(fileId, languageCode, branchName) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      await git.checkout({ fs: this.fs, dir, ref: branchName });
      return true;
    } catch (error) {
      console.error('Failed to checkout:', error);
      throw error;
    }
  }

  /**
   * Merge branches with conflict detection
   */
  async merge(fileId, languageCode, sourceBranch) {
    return this._enqueueFsOp(() => this._mergeImpl(fileId, languageCode, sourceBranch));
  }

  async _mergeImpl(fileId, languageCode, sourceBranch) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      const result = await git.merge({
        fs: this.fs,
        dir,
        ours: 'main',
        theirs: sourceBranch,
        author: this._currentAuthor(),
        abortOnConflict: false // Return conflicts instead of aborting
      });

      if (result.mergeCommit) {
        return { success: true, commit: result.mergeCommit };
      } else {
        return { success: false, conflicts: result.conflicts || [] };
      }
    } catch (error) {
      if (error.code === 'MergeConflictError' || error.code === 'MergeNotSupportedError' || error.name === 'MergeNotSupportedError') {
        return {
          success: false,
          conflicts: error.data || [],
          filepaths: error.filepaths || []
        };
      }
      throw error;
    }
  }

  /**
   * Delete a branch (used to clean up temporary merge branches -- see
   * versionHistoryService.mergeVersions()).
   */
  async deleteBranch(fileId, languageCode, branchName) {
    return this._enqueueFsOp(() => this._deleteBranchImpl(fileId, languageCode, branchName));
  }

  async _deleteBranchImpl(fileId, languageCode, branchName) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      await git.deleteBranch({ fs: this.fs, dir, ref: branchName });
      return true;
    } catch (error) {
      console.error('Failed to delete branch:', error);
      return false;
    }
  }

  /**
   * List all branches
   */
  async listBranches(fileId, languageCode) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      return await git.listBranches({ fs: this.fs, dir });
    } catch (error) {
      console.error('Failed to list branches:', error);
      return [];
    }
  }

  /**
   * Get current branch
   */
  async currentBranch(fileId, languageCode) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      const branch = await git.currentBranch({ fs: this.fs, dir });
      return branch || 'main';
    } catch (error) {
      console.error('Failed to get current branch:', error);
      return 'main';
    }
  }

  /**
   * Get current commit SHA (HEAD)
   */
  async getCurrentCommit(fileId, languageCode) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    try {
      return await git.resolveRef({ fs: this.fs, dir, ref: 'HEAD' });
    } catch (error) {
      // Expected on a fresh repo: HEAD points to refs/heads/main, which only
      // exists after the first commit. Not an error — there's just no HEAD yet.
      if (error?.code === 'NotFoundError' || error?.name === 'NotFoundError') {
        return null;
      }
      console.error('Failed to get current commit:', error);
      return null;
    }
  }

  // The server head's content as of the last fetch (refs/remotes/origin/main),
  // or null if there's no remote ref yet. Lets a caller check whether the
  // server's stored content is actually represented in its git history.
  async getRemoteHeadContent(fileId, languageCode) {
    const remoteOid = await this._enqueueFsOp(async () => {
      await this.init(fileId, languageCode);
      const dir = this._repoDir(fileId, languageCode);
      return git.resolveRef({ fs: this.fs, dir, ref: 'refs/remotes/origin/main' }).catch(() => null);
    });
    if (!remoteOid) return null;
    return this.getFileAtCommit(fileId, languageCode, remoteOid).catch(() => null);
  }

  /**
   * Read a file's content directly from the working tree (bypassing git
   * history) -- used by versionHistoryService.mergeVersions() to read the
   * result of an auto-resolved branch merge.
   */
  async readWorkingFile(fileId, languageCode) {
    await this.init(fileId, languageCode);
    return this._readWorkingFile(this._repoDir(fileId, languageCode));
  }

  /**
   * Push local commits to the real git server.
   */
  async push(fileId, languageCode) {
    return this._enqueueFsOp(() => this._pushImpl(fileId, languageCode));
  }

  async _pushImpl(fileId, languageCode) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    if (!localStorage.getItem('currentUser')) {
      console.warn('Not authenticated - cannot push');
      return { pushed: 0 };
    }

    const localOid = await this.getCurrentCommit(fileId, languageCode);
    if (!localOid) {
      return { pushed: 0 }; // nothing committed locally yet
    }

    // Skip the round-trip entirely if the last fetch (via pull()) already
    // shows the remote at our own HEAD -- nothing new to send.
    const remoteOid = await git.resolveRef({ fs: this.fs, dir, ref: 'refs/remotes/origin/main' }).catch(() => null);
    if (remoteOid && remoteOid === localOid) {
      return { pushed: 0 };
    }

    try {
      const result = await git.push({
        fs: this.fs,
        http,
        dir,
        url: this._remoteUrl(fileId, languageCode),
        ref: 'main',
        onAuth: getGitAuth,
        onAuthFailure: getGitAuthAfterFailure
      });
      return { pushed: 1, result };
    } catch (error) {
      // The server (git receive-pack) only fast-forwards -- a real
      // divergence here means someone else pushed between our last pull and
      // this push. sync()'s pull-then-push order makes this rare; when it
      // happens, the caller's next sync() will pull the new remote state and
      // merge before retrying.
      if (error?.name === 'PushRejectedError' || error?.code === 'PushRejectedError') {
        console.warn('Push rejected (remote has diverged) — will resolve on next sync:', error.message);
        return { pushed: 0, rejected: true };
      }
      console.error('Push failed:', error);
      throw error;
    }
  }

  /**
   * Pull commits from the real git server and merge.
   *
   * fetch() updates refs/remotes/origin/main; local/remote oids are then
   * compared via isDescendent/findMergeBase to decide fast-forward vs.
   * genuine divergence. Genuine divergence reuses _threeWayMerge (unchanged)
   * but now produces a real two-parent merge commit via git.commit's
   * low-level `parent` array, instead of reconstructing content as a new,
   * disconnected local commit.
   */
  async pull(fileId, languageCode) {
    return this._enqueueFsOp(() => this._pullImpl(fileId, languageCode));
  }

  async _pullImpl(fileId, languageCode) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    if (!localStorage.getItem('currentUser')) {
      console.warn('Not authenticated - cannot pull');
      return { pulled: 0 };
    }

    try {
      await git.fetch({
        fs: this.fs,
        http,
        dir,
        url: this._remoteUrl(fileId, languageCode),
        ref: 'main',
        singleBranch: true,
        tags: false,
        onAuth: getGitAuth,
        onAuthFailure: getGitAuthAfterFailure
      });
    } catch (error) {
      throw new Error(`Pull failed: ${error.message}`);
    }

    const remoteOid = await git.resolveRef({ fs: this.fs, dir, ref: 'refs/remotes/origin/main' }).catch(() => null);
    if (!remoteOid) {
      return { pulled: 0 }; // server has no history for this repo yet (shouldn't normally happen -- it seeds on first touch)
    }

    const localOid = await this.getCurrentCommit(fileId, languageCode);

    if (!localOid || localOid === remoteOid) {
      if (!localOid) {
        // No local commits yet — adopt remote history directly.
        await git.writeRef({ fs: this.fs, dir, ref: 'refs/heads/main', value: remoteOid, force: true });
        await git.checkout({ fs: this.fs, dir, ref: 'main', force: true });
        return { pulled: 1, fastForward: true };
      }
      return { pulled: 0 }; // already up to date
    }

    const remoteIsAhead = await git.isDescendent({ fs: this.fs, dir, oid: remoteOid, ancestor: localOid }).catch(() => false);
    if (remoteIsAhead) {
      await git.writeRef({ fs: this.fs, dir, ref: 'refs/heads/main', value: remoteOid, force: true });
      await git.checkout({ fs: this.fs, dir, ref: 'main', force: true });
      return { pulled: 1, fastForward: true };
    }

    const localIsAhead = await git.isDescendent({ fs: this.fs, dir, oid: localOid, ancestor: remoteOid }).catch(() => false);
    if (localIsAhead) {
      return { pulled: 0 }; // nothing to pull -- push() will send our commits
    }

    // Genuine divergence — 3-way merge, same as the old per-commit path, but
    // now the merge base is a real common ancestor commit and the result
    // (on a clean merge) is a real two-parent merge commit.
    const mergeBaseOids = await git.findMergeBase({ fs: this.fs, dir, oids: [localOid, remoteOid] }).catch(() => []);
    const baseOid = mergeBaseOids[0] || null;

    const ours = await this._readWorkingFile(dir);
    const theirs = await this.getFileAtCommit(fileId, languageCode, remoteOid).catch(() => '');

    let merged;
    if (baseOid) {
      const base = await this.getFileAtCommit(fileId, languageCode, baseOid).catch(() => '');
      merged = this._threeWayMerge(base, ours, theirs);
    } else if (ours === theirs) {
      merged = { result: ours, conflicts: false };
    } else {
      // No common ancestor -- local and remote are genuinely unrelated
      // histories. This is guaranteed on this migration's very first
      // reconciliation per file: the server lazily seeds its own
      // independent root commit from Postgres content the first time
      // ANYTHING touches that repo (gitRepoStore.js's ensureRepo),
      // unconnected to whatever local commit history this browser already
      // built up from real editing sessions. That seed commit never
      // represented anyone's actual edit -- it's a passive snapshot -- so
      // if it's a bare root commit with no parents of its own (i.e. nothing
      // has ever legitimately pushed a second commit on top of it), it's
      // safe to treat local as authoritative and connect the histories with
      // a real merge commit instead of surfacing an unresolvable conflict.
      // Diffing against a fabricated empty "base" isn't a real merge --
      // it reads as "insert everything," which duplicated content instead
      // of merging it (confirmed live). Only fall back to a genuine
      // conflict when remote already has real history of its own (another
      // client actually pushed there), where blindly preferring ours could
      // silently drop someone else's synced edits.
      const remoteCommit = await git.readCommit({ fs: this.fs, dir, oid: remoteOid }).catch(() => null);
      const remoteIsBareSeed = !!remoteCommit && remoteCommit.commit.parent.length === 0;
      merged = remoteIsBareSeed
        ? { result: ours, conflicts: false }
        : { result: null, conflicts: true };
    }
    // filepath kept in this shape for compatibility with syncService.js's
    // _reportGitPullConflicts, which parses fileId/languageCode back out of it.
    const filepath = `${fileId}/${languageCode}.txt`;

    if (merged.conflicts) {
      return {
        pulled: 1,
        merged: false,
        conflicts: true,
        conflictFiles: [filepath],
        conflictDetails: [{ filepath, content: merged.result, ours, theirs }]
      };
    }

    await this.fs.promises.writeFile(`${dir}/${CONTENT_FILE}`, merged.result, 'utf8');
    await git.add({ fs: this.fs, dir, filepath: CONTENT_FILE });
    const authorOf = async (oid) => {
      const commit = await git.readCommit({ fs: this.fs, dir, oid }).catch(() => null);
      return commit?.commit?.author?.name || 'Unknown';
    };
    const remoteAuthor = await authorOf(remoteOid);
    const localAuthor = await authorOf(localOid);
    const mergedBy = this._currentAuthor();
    const mergeSha = await git.commit({
      fs: this.fs,
      dir,
      message: this._mergeMessage(remoteAuthor, localAuthor, mergedBy.name),
      author: mergedBy,
      parent: [localOid, remoteOid]
    });

    return { pulled: 1, merged: true, conflicts: false, sha: mergeSha, remoteAuthor, localAuthor };
  }

  /**
   * Cherry-pick: apply changes from a specific commit onto the current HEAD.
   * Uses 3-way merge: base = commit's parent, ours = HEAD, theirs = commit.
   * Returns { success, sha, message } on clean merge, or
   *         { success: false, conflicts: true, mergedContent, message } when
   *         conflicts are detected (caller should show content to user).
   */
  async cherryPick(fileId, languageCode, commitSha) {
    return this._enqueueFsOp(() => this._cherryPickImpl(fileId, languageCode, commitSha));
  }

  async _cherryPickImpl(fileId, languageCode, commitSha) {
    await this.init(fileId, languageCode);
    const dir = this._repoDir(fileId, languageCode);

    // 1. Read the target commit metadata (to get parent SHA + message)
    const { commit } = await git.readCommit({ fs: this.fs, dir, oid: commitSha });
    const parentSha = commit.parent?.[0] ?? null;
    const originalMessage = commit.message.split('\n')[0].trim();

    // 2. Get file at the target commit (theirs)
    const theirs = await this.getFileAtCommit(fileId, languageCode, commitSha).catch(() => '');

    // 3. Get file at the parent commit (base) – empty string for root commits
    const base = parentSha
      ? await this.getFileAtCommit(fileId, languageCode, parentSha).catch(() => '')
      : '';

    // 4. Get current working file content (ours)
    const ours = await this._readWorkingFile(dir);

    // 5. Three-way merge
    const merged = this._threeWayMerge(base, ours, theirs);

    if (!merged.conflicts) {
      // Clean merge – write file, stage, commit
      await this.fs.promises.writeFile(`${dir}/${CONTENT_FILE}`, merged.result, 'utf8');
      await git.add({ fs: this.fs, dir, filepath: CONTENT_FILE });
      const sha = await git.commit({
        fs: this.fs,
        dir,
        message: `Cherry-pick: ${originalMessage}\n\nCherry-picked from ${commitSha.slice(0, 7)}`,
        author: this._currentAuthor(),
      });
      return { success: true, sha, message: `Cherry-pick: ${originalMessage}` };
    }

    // Conflicts – return the annotated content for the user to resolve
    return {
      success: false,
      conflicts: true,
      mergedContent: merged.result,
      message: `Cherry-pick from ${commitSha.slice(0, 7)}: ${originalMessage}`,
    };
  }

  /**
   * Internal line-based three-way merge.
   * base  = common ancestor (commit's parent)
   * ours  = current HEAD content
   * theirs = content we want to bring in (cherry-picked commit)
   *
   * Strategy:
   *   1. Try applyPatch (clean) using the diff package already in use.
   *   2. On failure, fall back to a simple line-level diff3 that emits
   *      <<<<<<<  >>>>>>>  conflict markers.
   */
  _threeWayMerge(base, ours, theirs) {
    // Fast paths
    if (ours === theirs) return { result: ours, conflicts: false };
    if (base === ours)   return { result: theirs, conflicts: false }; // ours unchanged → take theirs
    if (base === theirs) return { result: ours, conflicts: false };   // theirs unchanged → keep ours

    // Attempt clean patch apply (fuzzFactor 2 allows ±2 line offsets)
    try {
      const patch = Diff.createPatch('file', base, theirs, '', '');
      const patched = Diff.applyPatch(ours, patch, { fuzzFactor: 2 });
      if (patched !== false) return { result: patched, conflicts: false };
    } catch (_) { /* fall through */ }

    // Fallback: line-level diff3
    const oursLines   = ours.split('\n');

    const hunks = Diff.diffLines(base, theirs); // changes base→theirs
    const oursHunks = Diff.diffLines(base, ours); // changes base→ours

    // Build sets of base-line indices that ours modified
    const oursChanged = new Set();
    let bi = 0;
    for (const h of oursHunks) {
      if (h.removed) {
        for (let i = 0; i < (h.count || 1); i++) oursChanged.add(bi + i);
        bi += h.count || 1;
      } else if (h.added) {
        // added lines don't consume base indices
      } else {
        bi += h.count || 1;
      }
    }

    // Walk theirs-hunks and assemble result
    const resultLines = [];
    let hasConflicts = false;
    let baseIdx = 0;

    for (const hunk of hunks) {
      if (!hunk.added && !hunk.removed) {
        // Unchanged region – take from ours (may have ours-edits in same region)
        const oursChunk = oursLines.slice(baseIdx, baseIdx + (hunk.count || 1));
        resultLines.push(...oursChunk);
        baseIdx += hunk.count || 1;
      } else if (hunk.removed) {
        // Lines removed in theirs
        const count = hunk.count || 1;
        // Check if ours also changed these lines
        // eslint-disable-next-line no-loop-func -- baseIdx is read-only in this context
        const conflict = Array.from({ length: count }, (_, i) => oursChanged.has(baseIdx + i)).some(Boolean);
        if (conflict) {
          hasConflicts = true;
          const oursChunk = oursLines.slice(baseIdx, baseIdx + count);
          resultLines.push(
            '<<<<<<< ours',
            ...oursChunk,
            '=======',
            // theirs deleted these lines, so nothing here
            '>>>>>>> cherry-pick',
          );
        }
        // else: theirs deleted them, ours didn't change them → accept deletion (skip)
        baseIdx += count;
      } else if (hunk.added) {
        // Lines added in theirs – check if ours changed the surrounding region
        const hunkLines = (hunk.value || '').replace(/\n$/, '').split('\n');
        const surroundConflict = oursChanged.has(baseIdx - 1) || oursChanged.has(baseIdx);
        if (surroundConflict) {
          hasConflicts = true;
          resultLines.push(
            '<<<<<<< ours',
            ...oursLines.slice(Math.max(0, baseIdx - 1), baseIdx),
            '=======',
            ...hunkLines,
            '>>>>>>> cherry-pick',
          );
        } else {
          resultLines.push(...hunkLines);
        }
        // added lines don't advance baseIdx
      }
    }

    // Append any remaining ours lines not covered by hunks
    if (baseIdx < oursLines.length) {
      resultLines.push(...oursLines.slice(baseIdx));
    }

    return { result: resultLines.join('\n'), conflicts: hasConflicts };
  }

  /**
   * Sync: Pull then Push
   */
  async sync(fileId, languageCode) {
    try {
      const pullResult = await this.pull(fileId, languageCode);

      if (pullResult.conflicts) {
        return { ...pullResult, synced: false };
      }

      const pushResult = await this.push(fileId, languageCode);

      return {
        synced: true,
        pulled: pullResult.pulled,
        pushed: pushResult.pushed
      };
    } catch (error) {
      console.error('Sync failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
const gitService = new GitService();
export default gitService;
